import { useEffect, useState } from "react";
import type { QuestionnaireDetail } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type CustomerQuestionnaireState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; detail: QuestionnaireDetail };

/* No cache and no polling — same reasoning as useCustomerLabs. Every call to
   GET /clinic/patients/:id/questionnaire writes an asiakastietolaki 10 §
   käyttöloki row naming the staff member who read this customer's health
   questionnaire (see apps/api/src/modules/questionnaires/store.ts's
   patientQuestionnaireDetail). A background refresh would put entries in
   that register that no clinician actually performed. One fetch per time
   the pane is opened, which is exactly the disclosure that happened. */
export function useCustomerQuestionnaire(id: string | undefined): CustomerQuestionnaireState {
  const api = useApi();
  const [state, setState] = useState<CustomerQuestionnaireState>({ status: "loading" });

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.patients
      .questionnaire(id)
      .then((detail) => {
        if (!cancelled) {
          setState({ status: "success", detail });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load questionnaire" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  return state;
}
